import React from 'react';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { Button } from '@gik/ui';
import translationKeys from '@gik/i18n/en/core';
import i18n from '@gik/i18n';

export type LineClampingOptions = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10; // todo there must be a better way of doing that

export interface ILineClampProps {
  className?: string;
  lines: LineClampingOptions;
  showButton?: boolean;
  fadeOut?: boolean;
  block?: boolean;
  horizontal?: boolean;
  customButtonClick?: (ev: React.MouseEvent<HTMLDivElement>) => void;
  customButton?: (clamped: boolean) => React.ReactNode;
  tooltip?: boolean;
}

const blockName = 'line-clamp';

export default function LineClamp({
  className,
  children,
  lines,
  showButton = true,
  fadeOut = true,
  block,
  horizontal,
  customButton,
  customButtonClick,
  tooltip,
}: React.PropsWithChildren<ILineClampProps>) {
  const [clamped, setClamped] = React.useState<boolean>(true);
  const [overflows, setOverflows] = React.useState<boolean>(true);
  const [blockSize, setBlockSize] = React.useState<number>(null);
  const bem = useBemCN(blockName);

  const resizeObserver = React.useRef<ResizeObserver>(null);
  const textWrapperElRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (textWrapperElRef.current && !resizeObserver.current) {
      resizeObserver.current = new ResizeObserver(entries => {
        const entry = entries[0];
        setBlockSize((Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize).blockSize);
      });
      resizeObserver.current.observe(textWrapperElRef.current);
    }

    return () => {
      resizeObserver.current?.disconnect();
    };
  }, []);

  React.useEffect(() => {
    setTimeout(() => {
      // the if statement prevent a state update if the component was unmounted
      if (textWrapperElRef.current) setOverflows(textWrapperElRef.current?.scrollHeight > blockSize);
    });
  }, [lines, textWrapperElRef.current?.scrollHeight, blockSize]);

  return (
    <div
      {...bem(
        null,
        [
          { clamped },
          { isClamped: clamped && overflows },
          { block },
          { horizontal },
          { 'fade-out': overflows && fadeOut },
          { [`lines-${lines}`]: true },
        ],
        className
      )}
    >
      <div ref={textWrapperElRef} {...bem('text-wrapper')} title={tooltip && overflows && clamped && String(children)}>
        {children}
      </div>
      {showButton && !(clamped && !overflows) && (
        <>
          {customButton ? (
            <div
              {...bem('more-button')}
              onClick={customButtonClick ? customButtonClick : () => setClamped(clamped => !clamped)}
            >
              {customButton(clamped)}
            </div>
          ) : (
            <div {...bem('button')}>
              <Button variant="primary-plain" onClick={() => setClamped(clamped => !clamped)}>
                {i18n.t(clamped ? translationKeys.core.showMore : translationKeys.core.showLess)}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
